import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n-lite';
import { bindActionCreators } from 'redux';
import { BottomSheet, BottomSheetAction, BoxButton } from '@netexknowledge/smart-components';
import { ASSET_TYPES, PARAMS, ROUTES } from '../../const';
import CustomIcon from '../common/CustomIcon';
import routerHistory from '../../routerHistory';
import * as bookActions from '../../actions/bookActions';
import * as drawerActions from '../../actions/drawerActions';
import Dropzone from 'react-dropzone';
import * as assetsService from '../../services/assets/assetsService';
import { v4 } from 'uuid';
import { API_BACKOFFICE_ASSETS } from '../../services/api/apiConst';
import { ScormInstance } from '../../utils/scorm/scormInstance';
import * as messageAlert from '../../actions/messageAlertAction';
import { EncryptionNotSupportedError } from '../../utils/scorm/exceptions/EncryptionNotSupportedError';
import { NoIMSManifestError } from '../../utils/scorm/exceptions/NoIMSManifestError';
import { ANALYTICS_CONTEXT } from '../../types/analytics';

class NewContentPanel extends React.Component {
  static propTypes = {
    bookActions: PropTypes.any,
    messageAlert: PropTypes.any,
    classroom: PropTypes.any,
    customerId: PropTypes.number,
    creatingUnit: PropTypes.bool,
    drawerActions: PropTypes.any,
    hideImportUnitButton: PropTypes.bool,
    hideImportScormButton: PropTypes.bool,
    isRightPanelOpened: PropTypes.bool,
    book: PropTypes.any,
    // 'sheet': mobile bottom sheet instead of the right drawer layout
    variant: PropTypes.oneOf(['drawer', 'sheet'])
  };

  constructor(props, context) {
    super(props, context);
    this.createNewContent = this.createNewContent.bind(this);
    this.openLibrary = this.openLibrary.bind(this);
    this.closePanel = this.closePanel.bind(this);
    this.dropZoneSCORMRef = React.createRef();
  }

  closePanel = () => {
    const { drawerActions } = this.props;
    drawerActions.closeDrawerAndRemoveContent();
  };

  createNewContent = () => {
    const { classroom, bookActions, creatingUnit, bookId } = this.props;
    if (!creatingUnit) {
      bookActions.createCustomBook(classroom.id, I18n.t('rightPanel.newBook.defaultName'));
    } else {
      bookActions.addNewUnit(bookId, classroom.id, 0, undefined, undefined, ANALYTICS_CONTEXT.UNIT_CREATE);
    }
    this.closePanel();
  };

  openLibrary = () => {
    const { classroom, creatingUnit, bookId } = this.props;
    let routeClassroomId = `${ROUTES.CLASSROOM}/${classroom.id}`;
    let libraryUrl = creatingUnit
      ? `${routeClassroomId}/book/${bookId}/library?creating=unit`
      : `${routeClassroomId}/library`;
    this.closePanel();
    setTimeout(() => {
      routerHistory.replace(routerHistory.getCurrentLocation());
      routerHistory.push(libraryUrl);
    }, 0);
  };
  renderSCORMContainerDropZone = () => {
    return (
      <div style={{ display: 'none' }}>
        <Dropzone
          ref={this.dropZoneSCORMRef}
          disableClick={true}
          multiple={false}
          accept={'application/zip,application/x-zip-compressed'}
          onDrop={this.manageSCORMFile}
          style={{ width: '100%' }}
        ></Dropzone>
      </div>
    );
  };

  manageSCORMFile = async files => {
    const { customerId, bookActions, bookId, classroom, maxUploadScormSize, book } = this.props;

    event.preventDefault();
    this.closePanel();

    for (const file of files) {
      const sizeInMegas = assetsService.bytesToMegas(file.size);
      if (sizeInMegas > maxUploadScormSize) {
        this.props.messageAlert.error(`${I18n.t('upload.assetOverMaxSize')} ${maxUploadScormSize} Mb`);
      } else {
        const scormInstance = new ScormInstance();
        scormInstance
          .loadZipFile(file)
          .then(async () => {
            const defaultUuid = v4();
            const entries = scormInstance.getEntries();
            let parsedEntries = 0;

            const handleUploadContentStatus = status => {
              const x = status.percentCompleted;
              const y = parsedEntries;
              const z = entries.length;
              const formula = ((x / 100) * (y / z) + y / z) / 2;
              const globalStatus = {
                percentCompleted: parseInt(`${formula * 100}`),
                isUploading: status.isUploading,
                cancelUpload: status.cancelUpload
              };
              this.props.bookActions.setUploadContentStatus(globalStatus);
            };

            this.props.bookActions.setUploadContentStatus({
              percentCompleted: 0,
              isUploading: true,
              cancelUpload: false
            });

            const assetsToCreate = [];

            // Se crea la unidad
            const unit = await bookActions.addNewUnit(
              bookId,
              classroom.id,
              0,
              undefined,
              scormInstance.getSCORMTitle(),
              ANALYTICS_CONTEXT.SCORM_ADD
            );

            // Subimos todos los archivos
            for (const [fileName, zipEntry] of entries) {
              if (!zipEntry.dir) {
                const file = await scormInstance.getFileFromEntry(zipEntry, fileName);

                const shouldCreateAsset = scormInstance.getHTMLFileNames().includes(fileName);

                const assetMetadata = {
                  fileName: fileName,
                  customerId: customerId,
                  assetType: ASSET_TYPES.LESSON
                };

                // Si es un archivo candidato a ser un asset, se marca para crearlo más adelante
                if (shouldCreateAsset) {
                  assetsToCreate.push({
                    uuid: defaultUuid,
                    fileName: fileName,
                    assetType: ASSET_TYPES.LESSON,
                    mimeType: file.type
                  });
                }

                if (file) {
                  await assetsService
                    .uploadAsset(file, assetMetadata, {
                      handleUploadContentStatus,
                      createAssetAfterUpload: false,
                      defaultUuid
                    })
                    .catch(error => {
                      const title = scormInstance.getTitleByFileName(fileName);
                      this.props.messageAlert.error(`${I18n.t('upload.invalidFilenameOnZip')} ${title}`);
                      this.props.bookActions.deleteActivity(bookId, classroom.id, unit.activityId);
                      this.props.bookActions.setUploadContentStatus();
                      throw error;
                    });
                  parsedEntries++;
                }
              }
            }

            // Se crean los assets y las lecciones
            const webItems = scormInstance.getWebItemsByFileNames(assetsToCreate.map(a => a.fileName));
            for (let i = 0; i < webItems.length; i++) {
              const isLastItem = i === webItems.length - 1;

              const lessonTitle = await scormInstance.getTitleByItem(webItems[i]);

              const assetToCreate = assetsToCreate.find(
                a => a.fileName === scormInstance.getFileNameByItem(webItems[i])
              );

              if (assetToCreate) {
                await assetsService
                  .createAsset(
                    customerId,
                    classroom.id,
                    defaultUuid,
                    assetToCreate.fileName,
                    assetToCreate.assetType,
                    assetToCreate.mimeType,
                    isLastItem,
                    'SCORM'
                  )
                  .then(async asset => {
                    let baseUrl = API_BACKOFFICE_ASSETS.replace(PARAMS.ASSET, asset.uuid).replace(
                      PARAMS.CUSTOMER,
                      customerId
                    );

                    let url = new URL(baseUrl);
                    url.searchParams.set('bookActivityId', book.activityId);
                    url.searchParams.set('unitActivityId', unit.activityId);
                    url.searchParams.set('sectionId', classroom.id);

                    const additionalQueryParams = scormInstance.getAdditionalQueryParamsByFileName(
                      assetToCreate.fileName
                    );

                    if (additionalQueryParams) {
                      const encodedQueryParams = Object.keys(additionalQueryParams)
                        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(additionalQueryParams[key])}`)
                        .join('&');

                      const additionalQueryString = encodeURIComponent(encodedQueryParams);

                      url.searchParams.set('additionalQueryString', additionalQueryString);
                    }

                    await bookActions.addNewLesson(
                      lessonTitle,
                      url.toString(),
                      'text/html',
                      classroom.id,
                      unit.activityId,
                      ANALYTICS_CONTEXT.SCORM_ADD
                    );
                  });
              }
            }

            this.props.bookActions.setUploadContentStatus();
            this.props.messageAlert.success(I18n.t('upload.partialSuccess'));
            this.removeUploadedFile();
          })
          .catch(error => {
            this.removeUploadedFile();
            if (error instanceof EncryptionNotSupportedError) {
              this.props.messageAlert.error(I18n.t('upload.encryptionNotSupported'));
            } else if (error instanceof NoIMSManifestError) {
              this.props.messageAlert.error(I18n.t('upload.noIMSManifest'));
            }
          });
      }
    }
  };

  renderHeader = () => {
    return (
      <div className="header">
        <Link className="close" onClick={this.closePanel.bind(this)}>
          <CustomIcon name="close-button" size={24} />
        </Link>
        <span>{I18n.t('rightPanel.addContent.addContent')}</span>
      </div>
    );
  };

  openFileUpload = () => {
    this.dropZoneSCORMRef.current.open();
  };

  removeUploadedFile = () => {
    if (this.dropZoneSCORMRef.current) {
      this.dropZoneSCORMRef.current.setState({
        draggedFiles: [],
        acceptedFiles: [],
        rejectedFiles: []
      });
    }
  };

  // Mobile: same options as the drawer, as rows of a bottom sheet
  renderSheet = () => {
    const { creatingUnit, hideImportUnitButton, hideImportScormButton, isRightPanelOpened } = this.props;
    return (
      <BottomSheet
        open
        onClose={this.closePanel}
        closeLabel={I18n.t('common.close')}
        title={I18n.t('rightPanel.addContent.addContent')}
      >
        <BottomSheetAction
          icon={<i className="custom-icons-plus-button" />}
          label={I18n.t('rightPanel.addContent.newContent')}
          onClick={this.createNewContent}
        />
        {(!creatingUnit || !hideImportUnitButton) && (
          <BottomSheetAction
            icon={<i className="custom-icons-library" />}
            label={I18n.t('rightPanel.addContent.fromLibrary')}
            navigates
            onClick={this.openLibrary}
          />
        )}
        {/* The sheet stays open behind the file picker: manageSCORMFile closes it once a file is chosen */}
        {creatingUnit && !hideImportScormButton && (
          <BottomSheetAction
            icon={<i className="custom-icons-scorm-activity" />}
            label={'SCORM'}
            onClick={this.openFileUpload}
          />
        )}
        {isRightPanelOpened && this.renderSCORMContainerDropZone()}
      </BottomSheet>
    );
  };

  render() {
    const { creatingUnit, hideImportUnitButton, hideImportScormButton, isRightPanelOpened, variant } = this.props;
    if (variant === 'sheet') {
      return this.renderSheet();
    }
    return (
      <div className="right-panel-new" id="new-content-panel">
        <div className="content">
          {this.renderHeader()}
          <div className="heading1">
            <span>{I18n.t('rightPanel.newBook.chooseType')}</span>
          </div>
          <div className="add-resource-panel-buttons">
            <div className="add-resource-panel-buttons__one-item">
              <BoxButton
                id="addNewContent"
                label={I18n.t('rightPanel.addContent.newContent')}
                icon="custom-icons-plus-button"
                onClick={this.createNewContent}
              />
            </div>
            {(!creatingUnit || (creatingUnit && !hideImportUnitButton)) && (
              <div className="add-resource-panel-buttons__one-item">
                <BoxButton
                  id="addFromLibrary"
                  label={I18n.t('rightPanel.addContent.fromLibrary')}
                  icon="custom-icons-library"
                  onClick={this.openLibrary}
                />
              </div>
            )}
            {creatingUnit && !hideImportScormButton && (
              <div className="add-resource-panel-buttons__one-item">
                <BoxButton
                  id="addSCORM"
                  label={'SCORM'}
                  icon="custom-icons-scorm-activity"
                  onClick={this.openFileUpload}
                />
              </div>
            )}
          </div>
        </div>
        {isRightPanelOpened && this.renderSCORMContainerDropZone()}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    classroom: state.classrooms.selectedClassroom.classroom,
    hideImportUnitButton: state.account.configuration.hideImportUnitButton,
    customerId: state.account.configuration.customerId,
    hideImportScormButton: state.account.configuration.hideImportScormButton,
    maxUploadScormSize: state.account.configuration.maxUploadScormSize,
    isRightPanelOpened: state.drawer.open,
    book: state.book.book
  };
}

function mapDispatchToProps(dispatch) {
  return {
    drawerActions: bindActionCreators(drawerActions, dispatch),
    bookActions: bindActionCreators(bookActions, dispatch),
    messageAlert: bindActionCreators(messageAlert, dispatch),
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(NewContentPanel);
